import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/helpers';
import { Plus, Edit, Trash2, Search, MapPin, Upload, Image, X, Filter, Download, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const PLACE_TYPES = [
  { value: 'CITY', label: 'City' },
  { value: 'TOWN', label: 'Town' },
  { value: 'HILL_STATION', label: 'Hill Station' },
  { value: 'BEACH', label: 'Beach' },
  { value: 'PILGRIMAGE', label: 'Pilgrimage' },
  { value: 'WILDLIFE', label: 'Wildlife' },
  { value: 'BACKWATER', label: 'Backwater' },
  { value: 'HERITAGE', label: 'Heritage' },
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh'
];

const PlaceManager = () => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef(null);
  
  const [filters, setFilters] = useState({
    state: '',
    place_type: '',
  });
  
  // Bulk import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);
  
  const [formData, setFormData] = useState({
    country: 'India',
    state: '',
    place_name: '',
    place_type: 'CITY',
    description: '',
  });

  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async () => {
    try {
      setLoading(true);
      let url = '/places';
      const params = new URLSearchParams();
      if (filters.state) params.append('state', filters.state);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      let filteredPlaces = response.data;
      
      if (filters.place_type) {
        filteredPlaces = filteredPlaces.filter(p => p.place_type === filters.place_type);
      }
      
      setPlaces(filteredPlaces);
    } catch (error) {
      toast.error('Failed to load places');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async (placeId) => {
    try {
      const response = await api.get(`/upload/place/${placeId}/photos`);
      setPhotos(response.data);
    } catch (error) {
      console.error('Failed to load photos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPlace) {
        await api.put(`/places/${editingPlace.place_id}`, formData);
        toast.success('Place updated successfully');
      } else {
        await api.post('/places', formData);
        toast.success('Place created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchPlaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save place');
    }
  };

  const handleDelete = async (placeId) => {
    if (!window.confirm('Are you sure you want to delete this place?')) return;
    try {
      await api.delete(`/places/${placeId}`);
      toast.success('Place deleted successfully');
      fetchPlaces();
    } catch (error) {
      toast.error('Failed to delete place');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    try {
      await api.post(`/upload/place/${selectedPlace.place_id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photos uploaded successfully');
      fetchPhotos(selectedPlace.place_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await api.delete(`/upload/place/${selectedPlace.place_id}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchPhotos(selectedPlace.place_id);
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const resetForm = () => {
    setFormData({
      country: 'India',
      state: '',
      place_name: '',
      place_type: 'CITY',
      description: '',
    });
    setEditingPlace(null);
  };

  const handleEdit = (place) => {
    setEditingPlace(place);
    setFormData({
      country: place.country || 'India',
      state: place.state,
      place_name: place.place_name,
      place_type: place.place_type,
      description: place.description || '',
    });
    setDialogOpen(true);
  };

  const openPhotoDialog = async (place) => {
    setSelectedPlace(place);
    await fetchPhotos(place.place_id);
    setPhotoDialogOpen(true);
  };

  const applyFilters = () => {
    fetchPlaces();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ state: '', place_type: '' });
    fetchPlaces();
  };

  // Bulk import functions
  const downloadTemplate = async () => {
    try {
      const response = await api.get('/admin/bulk-import/places/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'places_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setValidationResult(null);
    setImportResult(null);
    
    // Validate the file
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/admin/bulk-import/places/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setValidationResult(response.data);
    } catch (error) {
      toast.error('Failed to validate file');
      setValidationResult({ valid: false, errors: [error.response?.data?.detail || 'Validation failed'] });
    }
  };

  const handleImport = async () => {
    if (!importFile || !validationResult?.valid) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await api.post('/admin/bulk-import/places', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      if (response.data.success) {
        toast.success(`Imported ${response.data.created} places, updated ${response.data.updated}`);
        fetchPlaces();
      }
    } catch (error) {
      toast.error('Import failed');
      setImportResult({ success: false, errors: [error.response?.data?.detail || 'Import failed'] });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setValidationResult(null);
    setImportResult(null);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const filteredPlaces = places.filter(
    (p) =>
      p.place_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.state.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlaceTypeLabel = (type) => {
    const found = PLACE_TYPES.find(t => t.value === type);
    return found?.label || type;
  };

  // Get unique states from places
  const uniqueStates = [...new Set(places.map(p => p.state))].sort();

  return (
    <Layout>
      <div className="space-y-6" data-testid="place-manager-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Place Manager</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">{filteredPlaces.length} places</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Dialog open={bulkImportOpen} onOpenChange={(open) => { setBulkImportOpen(open); if (!open) resetImport(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="bulk-import-places-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Import Places</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Download Template</p>
                      <p className="text-xs text-gray-500">CSV format with required columns</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                      <Download className="w-4 h-4 mr-2" />
                      Template
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Upload File (CSV or Excel)</Label>
                    <Input
                      ref={importFileRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                    />
                  </div>
                  
                  {validationResult && (
                    <div className={`p-3 rounded-lg ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {validationResult.valid ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <span className={`font-medium ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                          {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {validationResult.valid_rows} of {validationResult.total_rows} rows valid
                      </p>
                      {validationResult.errors?.length > 0 && (
                        <ul className="mt-2 text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                          {validationResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                  
                  {importResult && (
                    <div className={`p-3 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      <p className={`font-medium ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                        {importResult.success ? `Created: ${importResult.created}, Updated: ${importResult.updated}` : 'Import Failed'}
                      </p>
                      {importResult.errors?.length > 0 && (
                        <ul className="mt-2 text-xs text-red-600 list-disc list-inside">
                          {importResult.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBulkImportOpen(false)}>Close</Button>
                  <Button 
                    onClick={handleImport} 
                    disabled={!validationResult?.valid || importing}
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="add-place-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Place
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingPlace ? 'Edit Place' : 'Add New Place'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <SearchableSelect
                      options={INDIAN_STATES.map(s => ({ value: s, label: s }))}
                      value={formData.state}
                      onValueChange={(v) => setFormData({ ...formData, state: v })}
                      placeholder="Select state"
                      searchPlaceholder="Search states..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Place Name *</Label>
                    <Input
                      value={formData.place_name}
                      onChange={(e) => setFormData({ ...formData, place_name: e.target.value })}
                      placeholder="e.g., Munnar"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Place Type *</Label>
                    <Select
                      value={formData.place_type}
                      onValueChange={(v) => setFormData({ ...formData, place_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PLACE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Brief description of the place..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
                    <Button type="submit" className="flex-1">{editingPlace ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {showFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={filters.state || "all"} onValueChange={(v) => setFilters({ ...filters, state: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All states" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All states</SelectItem>
                      {INDIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Place Type</Label>
                  <Select value={filters.place_type || "all"} onValueChange={(v) => setFilters({ ...filters, place_type: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      {PLACE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={applyFilters} className="flex-1">Apply</Button>
                  <Button variant="outline" onClick={clearFilters}>Clear</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search places..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="place-search-input"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredPlaces.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No places found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Place Name</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">State</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Description</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlaces.map((place) => (
                      <tr key={place.place_id} className="border-b hover:bg-gray-50" data-testid={`place-row-${place.place_id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-green-600" />
                            </div>
                            <span className="font-medium">{place.place_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{place.state}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getPlaceTypeLabel(place.place_type)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600 max-w-xs truncate">{place.description || '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openPhotoDialog(place)} title="Photos" data-testid={`place-photos-btn-${place.place_id}`}>
                              <Image className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(place)} title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(place.place_id)} title="Delete">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Photo Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Photos - {selectedPlace?.place_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500" data-testid="place-photo-count">{photos.length}/10 photos</p>
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-1" />
                Upload Photos
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
                className="hidden"
                data-testid="place-photo-input"
              />
            </div>
            
            {photos.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Image className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No photos uploaded yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo) => (
                  <div key={photo.photo_id} className="relative group" data-testid={`place-photo-${photo.photo_id}`}>
                    <img src={getImageUrl(photo.photo_url)} alt="" className="w-full h-24 object-cover rounded" />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleDeletePhoto(photo.photo_id)}
                      data-testid={`place-photo-delete-${photo.photo_id}`}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PlaceManager;
